import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import InputSelect from "../../../components/InputSelect";
import InputDate from "../../../components/InputDate";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney } from "../../../helpers/conversions";
import { getRemainingAmount, isSettleable } from "../helpers";
import { usePaymentMethodOptions } from "../../../components/PaymentCondition/usePaymentOptions";

// Baixa em lote: quita o saldo em aberto dos títulos selecionados numa única
// conta/data. Ajustes finos (juros/desconto/parcial) ficam na baixa individual.
export const ModalSettleBatch = ({ show, close, entries, bankAccounts, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const [bankAccountId, setBankAccountId] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const { options: paymentMethodOptions } = usePaymentMethodOptions();
    const [settledAt, setSettledAt] = useState('');

    const settleableEntries = entries.filter(isSettleable);
    const total = settleableEntries.reduce((sum, entry) => sum + getRemainingAmount(entry), 0);

    useEffect(() => {
        if (!show) return;

        setBankAccountId(bankAccounts.length === 1 ? bankAccounts[0].id : null);
        setPaymentMethod(null);
        setSettledAt(moment().format('YYYY-MM-DD'));
    }, [show]);

    const save = () => {
        setSaving(true);

        api.post(`/finance-entry/settle-batch`, {
            settlements: settleableEntries.map(entry => ({
                finance_entry_id: entry.id,
                finance_bank_account_id: bankAccountId,
                payment_method_id: paymentMethod || null,
                amount: getRemainingAmount(entry),
                settled_at: settledAt,
            })),
        }).then(() => {
            toast.success(`${settleableEntries.length} ${settleableEntries.length > 1 ? 'títulos baixados' : 'título baixado'}`);
            close();
            onSaved();
        }).catch(renderError).then(() => setSaving(false));
    };

    const accountOptions = bankAccounts.map(account => ({ label: account.name, value: account.id }));

    return (
        <Modal
            title={`Baixar títulos em lote`}
            show={show}
            close={close}
            footer={
                <Button
                    type={`primary`}
                    size={`small`}
                    text={`Baixar ${settleableEntries.length} ${settleableEntries.length > 1 ? 'títulos' : 'título'}`}
                    action={save}
                    loading={saving}
                    disabled={!bankAccountId || settleableEntries.length === 0}
                />
            }
        >
            <div className="section">
                <div className="finance-settle-summary">
                    <div>
                        Títulos selecionados
                        <strong>{settleableEntries.length}</strong>
                    </div>
                    <div>
                        Total em aberto
                        <strong>{convertIntToMoney(total, true)}</strong>
                    </div>
                </div>

                {entries.length !== settleableEntries.length &&
                    <p style={{ fontSize: 13, marginBottom: 16 }}>
                        Títulos quitados ou cancelados foram ignorados na seleção.
                    </p>
                }

                <div className="row">
                    <div className="col-6">
                        <InputSelect menuInBody label={`Conta`} options={accountOptions} value={bankAccountId} change={setBankAccountId} />
                    </div>
                    <div className="col-6">
                        <InputDate label={`Data da baixa`} format={`YYYY-MM-DD`} value={settledAt} change={setSettledAt} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        {/* Vazio não apaga nada: o backend cai na forma prevista de cada título. */}
                        <InputSelect
                            menuInBody
                            label={`Forma de pagamento`}
                            options={[{ label: 'Manter a de cada título', value: null }, ...paymentMethodOptions]}
                            value={paymentMethod}
                            change={setPaymentMethod}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ModalSettleBatch;
